import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import {
  wsniperIcon, wtraderIcon, wpanelIcon, wscannerIcon,
  wclaimerIcon, wbridgeIcon, wcheckerIcon, wboostIcon,
} from '../assets';
import { EXTERNAL_LINKS } from '../config';
import './ToolsAccordion.css';

/* ─── Data ────────────────────────────────────────── */
const TOOLS = [
  {
    id: 'wpanel',
    num: '01',
    icon: wpanelIcon,
    name: { en: 'W·Panel', es: 'W·Panel' },
    headline: {
      en: 'Role Management',
      es: 'Gestión de Roles',
    },
    desc: {
      en: 'Tokenized role automation for NFT & HTS communities. Connect wallet, verify holdings, get roles automatically and discover your benefits.',
      es: 'Automatización de roles tokenizados para comunidades NFT & HTS. Conecta wallet, verifica holdings, obtén roles automáticamente y descubre tus beneficios.',
    },
    pills: {
      en: ['NFT & HTS Support', 'Discord App & Web Panel', 'Multitoken verification enabled', 'Hedera Official Partner', 'Multichain verification soon'],
      es: ['Soporte NFT & HTS', 'App en Discord & Panel Web', 'Verificación multitoken habilitada', 'Hedera Official Partner', 'Verificación multichain pronto'],
    },
    category: { en: 'Managers · Members', es: 'Managers · Miembros' },
    link: EXTERNAL_LINKS.PANEL,
    screenshot: '/screenshots/wpanel.png',
    accent: '#26E2B3',
  },  {
    id: 'wsniper',
    num: '02',
    icon: wsniperIcon,
    name: { en: 'W·Sniper', es: 'W·Sniper' },
    headline: {
      en: 'NFT Sniper',
      es: 'Sniper de NFTs',
    },
    desc: {
      en: 'Instant acquisition of rare Collectibles on Hedera through its reference marketplaces. Sub-200ms surgical buy order execution.',
      es: 'Adquisición instantánea de Colectibles raros en Hedera a través de sus mercados referencia. Ejecución en <200ms de órdenes de compra quirúrgicas.',
    },
    pills: {
      en: ['Auto best FP selection', 'Auto Execute', 'Unified rarity analytics · Multichain soon', 'Official SentX & Kábila partners', 'Free uses via missions'],
      es: ['Selección automática de mejor FP', 'Auto Ejecución', 'Analytics de rareza unificadas · Multichain pronto', 'Partners oficiales SentX y Kábila', 'Usos gratis por misiones'],
    },
    category: { en: 'NFT Trading', es: 'Trading NFT' },
    link: EXTERNAL_LINKS.TOOLS_PLATFORM,
    screenshot: '/screenshots/wsniper.png',
    accent: '#26E2B3',
  },  {
    id: 'wtrader',
    num: '03',
    icon: wtraderIcon,
    name: { en: 'W·Trader', es: 'W·Trader' },
    headline: {
      en: 'HTS Trading',
      es: 'Trading HTS',
    },
    desc: {
      en: 'Professional orderbook with limit orders and live charts. Multi-hop routing through SaucerSwap V1 & V2 and Eta Finance.',
      es: 'Libro de órdenes profesional con precio límite y gráficos en vivo. Routing multi-hop por SaucerSwap V1 & V2 y Eta Finance.',
    },
    pills: {
      en: ['Limit Orders', 'Live Charts', 'SaucerSwap V1/V2', 'Multi-Router', 'Stop-Limit Soon'],
      es: ['Órdenes Límite', 'Gráficos Live', 'SaucerSwap V1/V2', 'Multi-Router', 'Stop-Limit Próximo'],
    },
    category: { en: 'HTS Trading · DeFi', es: 'Trading HTS · DeFi' },
    link: EXTERNAL_LINKS.TOOLS_ORDERBOOK,
    screenshot: '/screenshots/wtrader.png',
    accent: '#a78bfa',
  },  {
    id: 'wscanner',
    num: '04',
    icon: wscannerIcon,
    name: { en: 'W·Scanner', es: 'W·Scanner' },
    headline: {
      en: 'On-Chain Analytics',
      es: 'Analíticas On-Chain',
    },
    desc: {
      en: 'Deep wallet analytics, market intelligence and live transaction tracking. HTS tokens, NFT collections and wallet scoring.',
      es: 'Analíticas profundas de wallet, inteligencia de mercado y seguimiento de transacciones. Tokens HTS, colecciones NFT y wallet scoring.',
    },
    pills: {
      en: ['Wallet Score', 'HTS Analysis', 'NFT Collections', 'Mirror Node', 'AML Module'],
      es: ['Wallet Score', 'Análisis HTS', 'Colecciones NFT', 'Mirror Node', 'Módulo AML'],
    },
    category: { en: 'Analytics · Compliance', es: 'Analíticas · Compliance' },
    link: EXTERNAL_LINKS.TOOLS_PLATFORM,
    screenshot: '/screenshots/wscanner.png',
    accent: '#38bdf8',
  },  {
    id: 'wclaimer',
    num: '05',
    icon: wclaimerIcon,
    name: { en: 'W·Claimer', es: 'W·Claimer' },
    headline: {
      en: 'Token Distribution',
      es: 'Distribución de Tokens',
    },
    desc: {
      en: 'Create and manage claimdrops at scale. Set conditions, expiration dates and participant limits for automated token distribution.',
      es: 'Crea y gestiona claimdrops a escala. Define condiciones, fechas de expiración y límites de participantes para distribución automatizada.',
    },
    pills: {
      en: ['Airdrop Creator', 'Claim Conditions', 'Expiration Dates', 'Unlimited Scale', 'Smart Contracts'],
      es: ['Creador de Airdrops', 'Condiciones Claim', 'Fechas Expiración', 'Escala Ilimitada', 'Smart Contracts'],
    },
    category: { en: 'Creators · Communities', es: 'Creadores · Comunidades' },
    link: EXTERNAL_LINKS.TOOLS_PLATFORM,
    screenshot: '/screenshots/wclaimer.png',
    accent: '#fbbf24',
  },  {
    id: 'wbridge',
    num: '06',
    icon: wbridgeIcon,
    name: { en: 'W·Bridge', es: 'W·Bridge' },
    headline: {
      en: 'Digital Collectibles Bridge',
      es: 'Bridge de Collectibles Digitales',
    },
    desc: {
      en: 'First digital collectibles bridge on Hedera. Transfer collections from Solana to Hedera and vice versa. Also bridge FT tokens and stables (USDC or USDT) between the same networks.',
      es: 'Primer Bridge de Collectibles digitales en Hedera. Traspasa colecciones de Solana a Hedera y viceversa. Bridgea también tokens FT y stables (USDC o USDT) entre las mismas redes.',
    },
    pills: {
      en: ['Liquidity aggregators', 'Soon 10+ EVM & L1 chains', 'Transfers 8–20s', 'Fixed fees by volume', 'Own Launchpad'],
      es: ['Agregadores de liquidez', 'Próximamente +10 cadenas EVM y L1', 'Transferencias 8–20s', 'Comisiones fijas por volumen', 'Launchpad Propio'],
    },
    category: { en: 'Infrastructure · DeFi', es: 'Infraestructura · DeFi' },
    link: null,
    comingSoon: true,
    screenshot: null,
    accent: '#60a5fa',
  },
];

/* ─── Row component ───────────────────────────────── */
const ToolRow = ({ tool, isOpen, onToggle, language }) => {
  const isEN = language === 'en';
  const name = tool.name[language] || tool.name.en;
  const headline = tool.headline[language] || tool.headline.en;
  const desc = tool.desc[language] || tool.desc.en;
  const pills = tool.pills[language] || tool.pills.en;
  const category = tool.category[language] || tool.category.en;

  return (
    <div
      className={`ta-row ${isOpen ? 'ta-row--open' : ''}`}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onToggle()}
      style={{ '--accent': tool.accent }}
    >
      {/* Collapsed header — always visible */}
      <div className="ta-row-header">
        <div className="ta-row-left">
          <img src={tool.icon} alt={name} className="ta-row-icon" />
          <span className="ta-row-name">{name}</span>
          <span className="ta-row-headline">{headline}</span>
        </div>
        <div className="ta-row-right">
          <span className="ta-row-category">{category}</span>
          <span className="ta-row-num">({tool.num})</span>
        </div>
      </div>

      {/* Expanded body — grid-template-rows CSS transition, zero distortion */}
      <div className="ta-row-body">
        <div className="ta-row-body-inner">
              {/* Left: description + pills + CTA */}
              <div className="ta-body-left">
                <p className="ta-body-desc">{desc}</p>
                <div className="ta-body-pills">
                  {pills.map(pill => (
                    <span key={pill} className="ta-pill">{pill}</span>
                  ))}
                </div>
                {tool.comingSoon ? (
                  <span className="ta-body-cta ta-body-cta--disabled">
                    {isEN ? 'Coming soon' : 'Próximamente'}
                  </span>
                ) : (
                  <a
                    href={tool.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ta-body-cta"
                    onClick={e => e.stopPropagation()}
                  >
                    {isEN ? 'Open tool' : 'Abrir herramienta'}
                    <ArrowUpRight size={14} />
                  </a>
                )}
              </div>

              {tool.screenshot && (
                <div className="ta-body-screen">
                  <div className="ta-monitor">
                    <div className="ta-monitor-bar">
                      <span className="ta-dot ta-dot--r" />
                      <span className="ta-dot ta-dot--y" />
                      <span className="ta-dot ta-dot--g" />
                      <span className="ta-monitor-url">
                        {name.toLowerCase().replace('·', '')}.whatalab.io
                      </span>
                    </div>
                    <img
                      src={tool.screenshot}
                      alt={`${name} screenshot`}
                      className="ta-monitor-img"
                      draggable={false}
                    />
                  </div>
                </div>
              )}
            </div>
        </div>
      </div>
  );
};

/* ─── Main ────────────────────────────────────────── */
const ToolsAccordion = () => {
  const { language } = useLanguage();
  const isEN = language === 'en';
  const [openId, setOpenId] = useState('wpanel'); // first open by default

  const toggle = id => setOpenId(prev => prev === id ? null : id);

  return (
    <section className="ta-section section" id="tools">
      <div className="container">

        {/* ── Section header: left sticky + right label ── */}
        <div className="ta-header">
          <div className="ta-header-left">
            <div className="ta-eyebrow">
              <span className="ta-eyebrow-dot" />
              {isEN ? 'the tools' : 'las herramientas'}
            </div>
            <h2 className="ta-title">
              {isEN
                ? <><span className="ta-title-accent">Tool Suite</span><br />Built to win.</>
                : <><span className="ta-title-accent">Suite de Herramientas</span><br />Construido para ganar.</>
              }
            </h2>
            <p className="ta-subtitle">
              {isEN
                ? 'Everything you need to trade, verify, automate and build on Hedera.'
                : 'Todo lo que necesitas para operar, verificar, automatizar y construir en Hedera.'
              }
            </p>
            <a
              href={EXTERNAL_LINKS.TOOLS_PLATFORM}
              target="_blank"
              rel="noopener noreferrer"
              className="ta-header-cta"
            >
              {isEN ? 'Launch Platform' : 'Abrir Plataforma'}
              <ArrowUpRight size={15} />
            </a>
          </div>

          {/* ── Accordion list ── */}
          <div className="ta-list">
            {TOOLS.map(tool => (
              <ToolRow
                key={tool.id}
                tool={tool}
                isOpen={openId === tool.id}
                onToggle={() => toggle(tool.id)}
                language={language}
              />
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default ToolsAccordion;
